import type { FieldBody, RecordBody } from '@/shared/api/generated/model';

/*
 * Чистые правила раздела «Формы» — отдельно от компонентов: их проверяют
 * тесты, а файлы компонентов экспортируют только компоненты.
 */

/** Поля заявки, которые видно в таблице и в окне: верхнего уровня, не скрытые. */
export function submissionFields(fields: FieldBody[]): FieldBody[] {
  return fields
    .filter((field) => field.parent_id === undefined && !field.hidden)
    .sort((a, b) => a.position - b.position);
}

/** Значение поля текстом — для ячейки таблицы и для CSV. */
export function valueText(value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (typeof value === 'boolean') {
    return value ? 'да' : 'нет';
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.map(valueText).filter(Boolean).join(', ');
  }

  return JSON.stringify(value);
}

function csvCell(value: string): string {
  return /[";\n\r]/.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
}

/**
 * Заявки в CSV для Excel: разделитель «;» и BOM — иначе русская Excel
 * сваливает всё в один столбец и показывает кракозябры вместо кириллицы.
 */
export function submissionsCSV(fields: FieldBody[], records: RecordBody[]): string {
  const head = ['Когда пришла', ...fields.map((field) => field.name), 'Новая'];
  const rows = records.map((record) => [
    new Date(record.created_at).toLocaleString('ru-RU'),
    ...fields.map((field) => valueText(record.data[field.key])),
    record.read_at === undefined ? 'да' : '',
  ]);

  return `\uFEFF${[head, ...rows].map((row) => row.map(csvCell).join(';')).join('\r\n')}\r\n`;
}

/** Пример значения поля — для подсказки «как отправить заявку». */
function sample(field: FieldBody): unknown {
  switch (field.widget) {
    case 'email':
      return 'ivan@example.com';
    case 'phone':
      return '+7 900 000-00-00';
    default:
  }

  switch (field.type) {
    case 'number':
      return 1;
    case 'boolean':
      return true;
    case 'date':
      return '2026-10-23';
    case 'datetime':
      return '2026-10-23T12:00:00Z';
    case 'url':
      return 'https://example.com';
    case 'text':
      return 'Здравствуйте! Хочу обсудить проект.';
    default:
      return field.key === 'page' ? 'https://example.com/contacts' : 'Иван';
  }
}

/** Тело заявки с примерами значений: скрытые поля с сайта не задаются. */
export function sampleBody(fields: FieldBody[]): Record<string, unknown> {
  return Object.fromEntries(
    submissionFields(fields)
      .filter(
        (field) =>
          field.options['private'] !== true &&
          !['group', 'blocks', 'richtext', 'image', 'file', 'reference'].includes(field.type),
      )
      .map((field) => [field.key, sample(field)]),
  );
}
